// The player page's header and cards (spec/dashboard.md section 11.2): the id with Copy, country, created, last seen, the
// online chip; Banned (M7 slice 3: reason, since, until, while the player has a ban), Bindings (values in full), Sessions,
// Collections (the roots of the tree); Imported from Balancy (M8 slice 2: the lazy import's record, R-MIG-06).
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { Player } from '../../api/types';
import { formatBytesShort } from '../../players/PlayerTree';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { IconButton } from '../../ui/Button';
import { Chip } from '../../ui/Chip';
import { formatInstant, formatInstantWithZone, useTimeZone } from '../../ui/time';
import { useToasts } from '../../ui/Toast';
import { banActive } from './commands';
import { Activity, Ban, Copy, Database, Link as Link2 } from '../../ui/icons';

export function OnlineChip({ player }: { player: Player }) {
  const { offsets } = useGame();
  const [zone] = useTimeZone();
  return (
    <span data-testid="online-chip">
      {player.online ? <span className="status-dot status-live">Online</span> : <span className="status-dot status-off">{`Offline · last seen ${player.lastSeenAt ? formatInstant(player.lastSeenAt, zone, { offsets }) : 'never'}`}</span>}
    </span>
  );
}

export function PlayerHeader({ player, actions }: { player: Player; actions?: ReactNode }) {
  const { offsets } = useGame();
  const [zone] = useTimeZone();
  const toasts = useToasts();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(player.id);
      toasts.success('Player id copied');
    } catch {
      toasts.error('The clipboard is not available');
    }
  };
  return (
    <header className="player-header" data-testid="player-header">
      <div className="page-head player-id-row">
        <h1>
          Player <code data-testid="player-id">{player.id}</code>
        </h1>
        <IconButton label="Copy the player id" onClick={() => void copy()}>
          <Copy aria-hidden="true" />
        </IconButton>
        <OnlineChip player={player} />
        {player.tester && (
          <span data-testid="tester-chip">
            <Chip tone="warning" title="A tester: excluded from every analytics counter and export unless a read includes testers">
              tester
            </Chip>
          </span>
        )}
        <span className="spacer" />
        {actions}
      </div>
      <dl className="kv player-facts">
        <dt>Country</dt>
        <dd>{player.country || 'unknown'}</dd>
        <dt>Created</dt>
        <dd>{formatInstantWithZone(player.createdAt, zone, { offsets })}</dd>
        <dt>Last seen</dt>
        <dd>{player.lastSeenAt ? formatInstantWithZone(player.lastSeenAt, zone, { offsets }) : 'never'}</dd>
      </dl>
    </header>
  );
}

export function PlayerCards({ env, player }: { env: string; player: Player }) {
  const { org, game, offsets } = useGame();
  const [zone] = useTimeZone();
  const at = (v: string | null | undefined) => formatInstant(v, zone, { offsets });
  const ban = player.ban ?? null;
  return (
    <div className="player-cards">
      {ban && (
        <section className="pane player-card player-ban" data-testid="player-ban" aria-label="Banned">
          <div className="pane-head">
            <Ban className="icon" aria-hidden="true" />
            <h2>
              Banned {banActive(ban) ? <Chip tone="danger">active</Chip> : <Chip>ended</Chip>}
            </h2>
          </div>
          <dl className="kv pane-body">
            <dt>Reason</dt>
            <dd>{ban.reason}</dd>
            <dt>Since</dt>
            <dd>{ban.since ? at(ban.since) : '—'}</dd>
            <dt>Until</dt>
            <dd>{ban.until ? at(ban.until) : 'until unbanned'}</dd>
          </dl>
        </section>
      )}
      {player.legacyImport && (
        <section className="pane player-card" data-testid="player-legacy-import" aria-label="Imported from Balancy">
          <div className="pane-head">
            <Database className="icon" aria-hidden="true" />
            <h2>Imported from Balancy</h2>
          </div>
          <dl className="kv pane-body">
            <dt>Balancy id</dt>
            <dd>
              <code className="binding-value">{player.legacyImport.legacyUserId}</code>
            </dd>
            <dt>When</dt>
            <dd>
              {at(player.legacyImport.importedAt)} on the {player.legacyImport.method} sign-in, in {player.legacyImport.durationMs} ms
            </dd>
            <dt>What</dt>
            <dd>
              {player.legacyImport.collections} collections, {player.legacyImport.keys} keys, {formatBytesShort(player.legacyImport.bytes)}
            </dd>
            {player.legacyImport.transformations.length > 0 && (
              <>
                <dt>Changed</dt>
                <dd>
                  <ul className="plain small">
                    {player.legacyImport.transformations.map((t) => (
                      <li key={t}>{t}</li>
                    ))}
                  </ul>
                </dd>
              </>
            )}
          </dl>
        </section>
      )}
      <section className="pane player-card" data-testid="player-bindings" aria-label="Bindings">
        <div className="pane-head">
          <Link2 className="icon" aria-hidden="true" />
          <h2>Bindings</h2>
        </div>
        {player.bindings.length === 0 ? (
          <p className="muted small pane-body">No binding.</p>
        ) : (
          <table className="table player-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Value</th>
                <th>Created</th>
                <th>Previous player</th>
              </tr>
            </thead>
            <tbody>
              {player.bindings.map((b) => (
                <tr key={`${b.type}:${b.value}`}>
                  <td>{b.type}</td>
                  <td>
                    <code className="binding-value">{b.value}</code>
                  </td>
                  <td>{at(b.createdAt)}</td>
                  <td>
                    {b.previousPlayerId ? (
                      <Link to={routes.player(org, game.slug, env, b.previousPlayerId)}>
                        <code>{b.previousPlayerId}</code>
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      <section className="pane player-card" data-testid="player-sessions" aria-label="Sessions">
        <div className="pane-head">
          <Activity className="icon" aria-hidden="true" />
          <h2>Sessions</h2>
        </div>
        <dl className="kv pane-body">
          <dt>Active</dt>
          <dd>{String(player.sessions?.active ?? 0)}</dd>
          <dt>Last opened</dt>
          <dd>{player.sessions?.lastOpenedAt ? at(player.sessions.lastOpenedAt) : 'never'}</dd>
        </dl>
      </section>
      <section className="pane player-card" data-testid="player-collections" aria-label="Collections">
        <div className="pane-head">
          <Database className="icon" aria-hidden="true" />
          <h2>Collections</h2>
        </div>
        {player.collections.length === 0 ? (
          <p className="muted small pane-body">No saves yet.</p>
        ) : (
          <table className="table player-table">
            <thead>
              <tr>
                <th>Collection</th>
                <th>Keys</th>
                <th>Bytes</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {player.collections.map((c) => (
                <tr key={c.collection}>
                  <td>
                    <code>{c.collection}</code>
                  </td>
                  <td>{String(c.keys)}</td>
                  <td>{formatBytesShort(c.bytes)}</td>
                  <td>{at(c.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
